import {
  formatDateTime,
  type JiraJobState,
  NO_PROJECT,
  routes,
  WORKLOG_TABS,
  type WorklogCounts,
  type WorklogList,
  type WorklogTab,
} from '@pmai/contracts';
import {
  Alert,
  Button,
  EmptyState,
  IconButton,
  Menu,
  MenuButton,
  PageHeader,
  Pager,
  SelectField,
  Spinner,
  Tabs,
  TextField,
} from '@pmai/ui';
import { useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useJiraBaseUrl } from '../../entities/jira/jira-links';
import { WORKLOG_VIEWS } from '../../entities/jira/worklog-views';
import { useApiMutation, useApiQuery } from '../../shared/api/hooks';
import { useBackgroundJob } from '../../shared/api/jobs';
import { useErrorText } from '../../shared/i18n';
import { WorklogCard } from './worklog-card';

const PAGE = 50;
const NO_FILTERS = { from: '', to: '', author: '', project: '' };
type Filters = typeof NO_FILTERS;
type Notice = { tone: 'success' | 'danger'; text: string };

const tabCount = (counts: WorklogCounts | undefined, tab: WorklogTab) => counts?.[tab] ?? 0;

/** Laika uzskaites saskaņošana (FR-JIRA-007…021). */
export function ReconciliationPage() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const baseUrl = useJiraBaseUrl();
  const [tab, setTab] = useState<WorklogTab>('pending');
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [offset, setOffset] = useState(0);
  const [notice, setNotice] = useState<Notice | null>(null);

  const query = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
  const list = useApiQuery(
    routes.worklogs.list,
    { query: { tab, ...query, offset, limit: PAGE } },
    { placeholderData: (previous) => previous },
  );
  const projects = useApiQuery(routes.projects.options);
  const resources = useApiQuery(routes.resources.list);
  const load = useBackgroundJob(routes.worklogs.load, routes.worklogs.state, {
    invalidates: WORKLOG_VIEWS,
  });
  const resolve = useApiMutation(routes.worklogs.resolve, { invalidates: WORKLOG_VIEWS });

  const loaded = (state: JiraJobState): Notice =>
    state.lastError || !state.lastResult
      ? { tone: 'danger', text: state.lastError ?? t('errors.INTERNAL') }
      : {
          tone: 'success',
          text: t('reconciliation.loaded', {
            ...state.lastResult,
            mode: t(`reconciliation.mode.${state.lastResult.mode}`),
          }),
        };
  const startLoad = (full: boolean) => {
    setNotice(null);
    load.run.mutate(
      { body: { full } },
      {
        onSuccess: (state) => setNotice(loaded(state)),
        onError: (error) => setNotice({ tone: 'danger', text: errorText(error) }),
      },
    );
  };
  const startResolve = () => {
    setNotice(null);
    resolve.mutate(
      {},
      {
        onSuccess: (r) => setNotice({ tone: 'success', text: t('reconciliation.resolved', r) }),
        onError: (error) => setNotice({ tone: 'danger', text: errorText(error) }),
      },
    );
  };
  const change = (next: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...next }));
    setOffset(0);
  };
  const filtered = Object.keys(query).length > 0;
  const data = list.data;

  return (
    <>
      <PageHeader
        title={t('reconciliation.title')}
        actions={
          <div className="flex items-center gap-2">
            <Button icon="refresh-cw" loading={load.running} onClick={() => startLoad(false)}>
              {t('reconciliation.load')}
            </Button>
            <Menu
              label={t('reconciliation.more')}
              trigger={<IconButton icon="ellipsis" variant="secondary" label={t('reconciliation.more')} />}
            >
              <MenuButton icon="download" onSelect={() => startLoad(true)}>
                {t('reconciliation.fullLoad')}
              </MenuButton>
              <MenuButton icon="list-checks" onSelect={startResolve}>
                {t('reconciliation.resolve')}
              </MenuButton>
            </Menu>
          </div>
        }
      />
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-muted" data-testid="reconciliation-meta">
          {metaLine(t, load.state.data, data?.counts)}
        </p>
        {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
        <Tabs
          label={t('reconciliation.title')}
          value={tab}
          onChange={(v) => {
            setTab(v);
            setOffset(0);
          }}
          options={WORKLOG_TABS.map((v) => ({
            value: v,
            label: t(`reconciliation.tabs.${v}`),
            count: tabCount(data?.counts, v),
            highlight: v === 'flagged',
          }))}
        />
        <WorklogFilters filters={filters} data={data} onChange={change} />
        {data === undefined ? (
          <Spinner label={t('common.loading')} />
        ) : data.rows.length === 0 ? (
          <EmptyState
            icon="clock"
            title={filtered ? t('reconciliation.emptyFiltered') : t('reconciliation.empty')}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {data.rows.map((w) => (
              <WorklogCard
                key={`${w.id}:${w.reviewStatus}:${w.projectId}:${w.resourceId}:${w.reviewNote}`}
                worklog={w}
                projects={projects.data ?? []}
                resources={resources.data ?? []}
                baseUrl={baseUrl}
              />
            ))}
            <Pager offset={offset} limit={PAGE} total={data.total} onChange={setOffset} />
          </div>
        )}
      </div>
    </>
  );
}

function metaLine(t: TFunction, state: JiraJobState | undefined, counts: WorklogCounts | undefined): string {
  if (!state?.lastRunAt) return t('reconciliation.neverLoaded');
  return [
    t('reconciliation.lastRun', { at: formatDateTime(state.lastRunAt) }),
    state.lastFullSyncAt && t('reconciliation.lastFull', { at: formatDateTime(state.lastFullSyncAt) }),
    t('reconciliation.totalCount', { total: counts?.all ?? 0 }),
    counts?.deleted ? t('reconciliation.deletedCount', { deleted: counts.deleted }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Date, author and project filters with "Rāda X no Y" (FR-JIRA-016). */
function WorklogFilters({
  filters,
  data,
  onChange,
}: {
  filters: Filters;
  data: WorklogList | undefined;
  onChange: (next: Partial<Filters>) => void;
}) {
  const { t } = useTranslation();
  const active = Object.values(filters).some((v) => v !== '');
  return (
    <div role="search" aria-label={t('reconciliation.filters')} className="flex flex-wrap items-end gap-3">
      <TextField
        label={t('reconciliation.from')}
        type="date"
        value={filters.from}
        onChange={(e) => onChange({ from: e.target.value })}
      />
      <TextField
        label={t('reconciliation.to')}
        type="date"
        value={filters.to}
        onChange={(e) => onChange({ to: e.target.value })}
      />
      <div className="w-56">
        <SelectField
          label={t('reconciliation.author')}
          placeholder={t('reconciliation.all')}
          value={filters.author}
          onChange={(e) => onChange({ author: e.target.value })}
          options={(data?.authors ?? []).map((a) => ({ value: a, label: a }))}
        />
      </div>
      <div className="w-64">
        <SelectField
          label={t('reconciliation.projectFilter')}
          placeholder={t('reconciliation.all')}
          value={filters.project}
          onChange={(e) => onChange({ project: e.target.value })}
          options={[
            ...(data?.projects ?? []).map((p) => ({ value: p.id, label: `${p.name} (${p.customerName})` })),
            ...(data?.hasUnmapped ? [{ value: NO_PROJECT, label: t('reconciliation.noProject') }] : []),
          ]}
        />
      </div>
      <Button variant="ghost" size="sm" onClick={() => onChange(NO_FILTERS)}>
        {t('reconciliation.clearFilters')}
      </Button>
      {active && data && (
        <span className="text-sm text-ink-muted" data-testid="reconciliation-shown">
          {t('reconciliation.shown', { shown: data.total, of: data.tabTotal })}
        </span>
      )}
    </div>
  );
}
